import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useEffect } from 'react';
import { StylesProvider } from '@material-ui/styles';
import responsiveFontSizes from '@material-ui/core/styles/responsiveFontSizes';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { createTheme } from '@material-ui/core/styles';
import cssVars from 'css-vars-ponyfill';
import jm from './jmm.module.scss';
import truck from './truck.module.scss';

// This is a weird helper function, as JS behaves king of quirky with the require syntax when it is used in es modules but compiled to CommonJS
// const interopRequireDefault = obj => (obj && obj.__esModule ? obj.default : obj)

/* Context */
import { jsx as _jsx } from "react/jsx-runtime";
export var ThemeContext = /*#__PURE__*/React.createContext({});
/**
 * Global theme variant provider
 *
 **/
var ThemeProvider = function ThemeProvider(_ref) {
  var children = _ref.children,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'pc' : _ref$variant,
    subvariant = _ref.subvariant,
    muiThemeOverride = _ref.muiThemeOverride;
  var generalStyles;
  // For compatibility with truck-jobs.com components
  if (variant === 'pc' || variant === 'bc') {
    generalStyles = truck;
  } else {
    //For compatibility with Jobmatch.me components
    generalStyles = jm;
  }
  useEffect(function () {
    cssVars({
      variables: generalStyles,
      watch: true
    });
  }, [generalStyles]);
  var selectTheme = function selectTheme(variant) {
    return muiThemes[variant] || muiThemes.truck;
  }; // With fallback to truck styles

  var injectMuiThemeOverride = function injectMuiThemeOverride(theme) {
    return muiThemeOverride ? _extends({}, theme, muiThemeOverride(generalStyles)) : theme;
  };
  var commonOverrides = {
    MuiPaper: {
      rounded: {
        borderRadius: '10px'
      },
      elevation1: {
        boxShadow: "-1px 3px 10px ".concat(generalStyles.shadowColor)
      }
    }
  };
  var muiThemes = {
    jmm: {
      palette: {
        primary: {
          main: generalStyles.primary
        },
        secondary: {
          main: generalStyles.secondary
        },
        // Used by `getContrastText()` to maximize the contrast between
        // the background and the text.
        //contrastThreshold: 3,
        // Used by the functions below to shift a color's luminance by approximately
        // two indexes within its tonal palette.
        // E.g., shift from Red 500 to Red 300 or Red 700.
        tonalOffset: 0.2,
        common: {
          black: generalStyles.text
        },
        text: {
          primary: generalStyles.dark
        }
      },
      spacing: 2,
      typography: {
        fontFamily: generalStyles.fontFamilyBase,
        h1: {
          fontSize: '68px',
          marginBottom: '30px',
          fontWeight: 600,
          lineHeight: 1
        },
        h2: {
          fontSize: '48px',
          marginBottom: '30px',
          fontWeight: 600
        },
        h3: {
          fontSize: '48px',
          marginBottom: '30px',
          fontWeight: 600
        }
      },
      overrides: _extends({}, commonOverrides, {
        MuiSlider: {
          thumb: {
            backgroundColor: generalStyles.dark,
            marginTop: '-9px!important',
            marginLeft: '-13px!important',
            height: '30px',
            width: '30px'
          },
          track: {
            backgroundColor: generalStyles.primary,
            borderRadius: '6px',
            height: '12px'
          },
          rail: {
            color: generalStyles.light,
            border: "1px solid ".concat(generalStyles.dark),
            borderRadius: '6px',
            height: '12px'
          }
        },
        MuiChip: {
          sizeSmall: {
            fontSize: generalStyles['fontSize-base-extra-small'],
            fontFamily: generalStyles.fontFamilyBaseBold
          }
        },
        MuiPaper: {
          rounded: {
            borderRadius: '2rem'
          }
        },
        MuiCardContent: {
          root: {
            padding: '2rem'
          }
        },
        MuiCardHeader: {
          root: {
            padding: '2rem'
          }
        }
      })
    },
    // Truck Theme
    truck: {
      palette: {
        primary: {
          main: generalStyles.primary || '#052c48'
        },
        secondary: {
          main: generalStyles.secondary || '#20aee2',
          contrastText: generalStyles.light || '#fff'
        }
      },
      typography: {
        fontFamily: generalStyles.fontFamilyBase,
        useNextVariants: true
      },
      overrides: _extends({}, commonOverrides, {
        MuiInput: {
          underline: {
            '&:before': {
              borderBottom: 'none'
            },
            '&:hover:not($disabled):not($error):not($focused):before': {
              borderBottom: 'none'
            },
            '&:after': {
              borderBottom: 'none'
            }
          },
          '&$focused': {
            borderBottom: 'none'
          }
        },
        MuiSlider: {
          thumb: {
            backgroundColor: generalStyles.primary,
            marginTop: '-9px!important',
            marginLeft: '-13px!important',
            height: '30px',
            width: '30px'
          },
          track: {
            backgroundColor: generalStyles.dark,
            borderRadius: '6px',
            height: '12px'
          },
          rail: {
            color: generalStyles.light,
            border: "2px solid ".concat(generalStyles.dark),
            borderRadius: '6px',
            height: '12px'
          }
        }
      })
    },
    // Care Theme
    care: {
      palette: {
        primary: {
          main: generalStyles.primary
        },
        secondary: {
          main: generalStyles.secondary
        },
        // Used by `getContrastText()` to maximize the contrast between
        // the background and the text.
        // contrastThreshold: 3,
        // Used by the functions below to shift a color's luminance by approximately
        // two indexes within its tonal palette.
        // E.g., shift from Red 500 to Red 300 or Red 700.
        tonalOffset: 0.2,
        common: {
          black: generalStyles.text
        }
      },
      spacing: 2,
      typography: {
        fontFamily: generalStyles.fontFamilyBase,
        h1: {
          fontSize: '68px',
          marginBottom: '30px',
          fontWeight: 600,
          lineHeight: 1
        },
        h2: {
          fontSize: '48px',
          marginBottom: '30px',
          fontWeight: 600
        },
        h3: {
          fontSize: '38px',
          marginBottom: '30px',
          fontWeight: 600
        },
        h4: {
          fontSize: '28px',
          marginBottom: '30px',
          fontWeight: 600
        }
      },
      overrides: _extends({}, commonOverrides, {
        MuiSlider: {
          thumb: {
            backgroundColor: generalStyles.dark,
            marginTop: '-9px!important',
            marginLeft: '-13px!important',
            height: '30px',
            width: '30px'
          },
          track: {
            backgroundColor: generalStyles.primary,
            borderRadius: '6px',
            height: '12px'
          },
          rail: {
            color: generalStyles.light,
            border: "1px solid ".concat(generalStyles.dark),
            borderRadius: '6px',
            height: '12px'
          }
        }
      })
    }
  };
  var _useState = useState({
      variant: variant,
      subvariant: subvariant || '',
      muiTheme: responsiveFontSizes(createTheme(injectMuiThemeOverride(selectTheme(variant))))
    }),
    _useState2 = _slicedToArray(_useState, 1),
    state = _useState2[0];
  return /*#__PURE__*/_jsx(StylesProvider, {
    injectFirst: true,
    children: /*#__PURE__*/_jsx(MuiThemeProvider, {
      theme: state.muiTheme,
      children: /*#__PURE__*/_jsx(ThemeContext.Provider, {
        value: state,
        children: /*#__PURE__*/_jsx("div", {
          style: {
            fontFamily: generalStyles.fontFamilyBase
          },
          children: children
        })
      })
    })
  });
};

/**
 * The consumer component for the ThemeContext
 **/
var ThemeConsumer = ThemeContext.Consumer;

/**
 * HOC that provides the globalVariant Prop
 */
export var withThemeVariant = function withThemeVariant(Component) {
  return function (props) {
    return /*#__PURE__*/_jsx(ThemeConsumer, {
      children:
      // Components can be used without provider. HOC needs to be nullsave
      function children() {
        var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
          variant: '',
          subvariant: '',
          muiTheme: {}
        };
        return /*#__PURE__*/_jsx(Component, _extends({
          globalThemeVariant: state.variant,
          globalThemeSubVariant: state.subvariant
        }, props));
      }
    });
  };
};
export default ThemeProvider;